import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import filters from '../../../lib/filters'
import * as projectsApi from '@/v31-buyer/sdk.gen'
import {
  FullProject,
  Category,
  PublishProject,
  NewProject,
} from '@/v31-buyer/types.gen'

export const useProjectStore = defineStore('project', () => {
  // State as refs
  const activeProject = ref<FullProject>(null)
  const projectFormData = ref<NewProject>({
    title: '',
  })
  const eventTypes = ref<string[]>([])

  const eventTypeLookup = computed(() => {
    if (!eventTypes.value) return {}
    return Object.fromEntries(
      eventTypes.value.map((et) => [et, filters.underscoredToTitle(et)])
    )
  })

  const canManageProjectAttachments = computed(() => {
    return (
      activeProject.value &&
      ['Draft', 'Live'].includes(activeProject.value.status)
    )
  })

  const canManageResponseAttachments = computed(() => {
    return (
      activeProject.value &&
      ['Live', 'Closed'].includes(activeProject.value.status) &&
      !activeProject.value.deadline
    )
  })

  const canViewScore = computed(() => {
    return (
      activeProject.value &&
      ['Live', 'Closed'].includes(activeProject.value.status)
    )
  })

  const canViewAnswer = computed(() => {
    return true // TODO put this back as and when it is implemented fully
    /* (
      activeProject.value &&
      activeProject.value.deadline &&
      new Date() >= new Date(activeProject.value.deadline)
    )*/
  })

  const isCurrentProjectLive = computed(() => {
    return activeProject.value?.status === 'Live'
  })

  const haveMultiScoresets = computed(() => {
    return activeProject.value?.multiscored
  })

  // Actions as regular functions
  function setProject(project: FullProject) {
    activeProject.value = project
  }

  function setProjectFormData(data: NewProject) {
    projectFormData.value = data
  }

  function publishCurrentProject() {
    if (activeProject.value && activeProject.value.status) {
      activeProject.value.status = 'Live'
    }
  }

  function closeCurrentProject() {
    if (activeProject.value) {
      activeProject.value.status = 'Closed'
    }
  }

  function clearCurrentProject() {
    activeProject.value = null
  }

  function setEventTypes(newEventTypes: string[]) {
    eventTypes.value = newEventTypes
  }

  // New function to fetch project data using the v31-buyer API
  async function fetchProject(projectId: number) {
    const response = await projectsApi.getProject({
      path: { project_id: projectId },
    })

    setProject(response.data)
    return response.data
  }

  async function assignCategoryToProject(params: {
    projectId: number
    categoryId: number
    categoryMap: Record<number, Category>
  }) {
    if (!activeProject.value || activeProject.value.id !== params.projectId)
      return

    // Call the API to persist the category assignment using the new client
    await projectsApi.putProjectCategory({
      path: {
        project_id: params.projectId,
        category_id: params.categoryId,
      },
    })

    // Update local state after successful API call
    const catToAssign = params.categoryMap[params.categoryId]
    if (!catToAssign)
      throw new Error(`Category with id ${params.categoryId} not found`)
    if (!activeProject.value.categories) activeProject.value.categories = []
    activeProject.value.categories.push(catToAssign)
  }

  async function removeCategoryFromProject(params: {
    projectId: number
    categoryId: number
  }) {
    if (!activeProject.value || activeProject.value.id !== params.projectId)
      return

    // Call the API to persist the category removal using the new client
    await projectsApi.deleteProjectCategory({
      path: {
        project_id: params.projectId,
        category_id: params.categoryId,
      },
    })

    // Update local state after successful API call
    const catIdx = activeProject.value.categories.findIndex(
      (cat: Category) => cat.id === params.categoryId
    )
    if (catIdx === -1)
      throw new Error(`Category with id ${params.categoryId} not found`)
    activeProject.value.categories.splice(catIdx, 1)
  }

  async function publishProject(params: {
    projectId: number
    issueIds: number[]
  }) {
    const publishData: PublishProject = {
      release_issue_ids: params.issueIds,
    }

    const result = await projectsApi.putProjectPublish({
      path: {
        project_id: params.projectId,
      },
      body: publishData,
    })

    // Update local state if this is the active project
    if (activeProject.value && activeProject.value.id === params.projectId) {
      activeProject.value.status = 'Live'
    }

    return result.data
  }

  function updateProjectFormField<K extends keyof NewProject>(
    field: K,
    value: NewProject[K]
  ) {
    projectFormData.value[field] = value
  }

  function updateProjectForm(updates: Partial<NewProject>) {
    Object.assign(projectFormData.value, updates)
  }

  // Return everything that should be exposed
  return {
    // State
    activeProject,
    projectFormData,
    eventTypes,

    // Getters
    eventTypeLookup,
    canManageProjectAttachments,
    canManageResponseAttachments,
    canViewScore,
    canViewAnswer,
    isCurrentProjectLive,
    haveMultiScoresets,

    // Actions
    setProject,
    setProjectFormData,
    publishCurrentProject,
    closeCurrentProject,
    clearCurrentProject,
    setEventTypes,
    fetchProject,
    assignCategoryToProject,
    removeCategoryFromProject,
    publishProject,
    updateProjectFormField,
    updateProjectForm,
  }
})
